import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { pulpouBrand } from '../config/brandConfig';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface StatisticsData {
  totalPublicaciones: number;
  publicacionesConDescuento: number;
  porcentajeDescuento: number;
  descuentoPromedio: number;
  precioMediano: number;
  precioMinimo: {
    valor: number;
    producto: any;
  };
  precioMaximo: {
    valor: number;
    producto: any;
  };
  cantidadMarcas: number;
  cantidadCategorias: number;
  shareMarcias: {
    data: Array<{
      marca: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
  shareCategorias: {
    data: Array<{
      categoria: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
  publicacionesPorSitio: {
    data: Array<{
      sitio: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
  rangosPreciosData?: {
    data: Array<{
      rango: string;
      cantidad: number;
      porcentaje: number;
      desde: number;
      hasta: number;
    }>;
    totalPublicaciones: number;
  };
  installmentsDistribution?: {
    data: Array<{
      installments: number | null;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
    hasInstallmentsColumn?: boolean;
  };
}

interface ExportOptions {
  tableName: string;
  tableDate: string;
  filters: any;
  statistics: StatisticsData;
  t: (key: string, options?: any) => string;
  activeFilterName?: string | null;
  clientName?: string;
}

export class PDFGenerator {
  private pdf: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 20;
  private currentY: number = 20;
  private primaryColor = '#3B82F6';
  private secondaryColor = '#1E40AF';
  private brandLogoUrl = '/brand.svg';
  private pulpouLogoVerticalUrl = '/Logo_pulpou_vertical.svg';
  private pulpouLogoHorizontalUrl = '/logo_pulpou.svg';
  
  constructor() {
    console.log('PDFGenerator constructor called');
    console.log('jsPDF available:', typeof jsPDF);
    
    try {
      this.pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      console.log('jsPDF instance created');
      console.log('autoTable method available:', typeof this.pdf.autoTable);
      
      // Check if autoTable is available
      if (!this.pdf.autoTable) {
        console.error('autoTable method not found on jsPDF instance');
      }
      
      this.pageWidth = this.pdf.internal.pageSize.getWidth();
      this.pageHeight = this.pdf.internal.pageSize.getHeight();
      
      console.log('Page dimensions:', this.pageWidth, 'x', this.pageHeight);
      
      // Load logos
      this.loadLogos();
    } catch (error) {
      console.error('Error creating jsPDF instance:', error);
      throw error;
    }
  }

  private async loadLogos(): Promise<void> {
    try {
      console.log('Logos will be loaded when needed');
    } catch (error) {
      console.error('Error loading logos:', error);
    }
  }

  private async loadImageAsBase64(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', url, error);
      return '';
    }
  }

  private async addSvgImage(url: string, x: number, y: number, maxWidth: number, maxHeight?: number, maintainAspectRatio: boolean = true): Promise<void> {
    try {
      const img = new Image();
      
      return new Promise((resolve) => {
        img.onload = () => {
          // Get natural dimensions
          const naturalWidth = img.naturalWidth || img.width;
          const naturalHeight = img.naturalHeight || img.height;
          
          console.log(`Loading ${url}: natural dimensions ${naturalWidth}x${naturalHeight}`);
          
          let finalWidth = maxWidth;
          let finalHeight = maxHeight || maxWidth;
          
          if (maintainAspectRatio && naturalWidth && naturalHeight) {
            const aspectRatio = naturalWidth / naturalHeight;
            
            if (maxHeight) {
              // Fit within both maxWidth and maxHeight
              const widthBasedHeight = maxWidth / aspectRatio;
              const heightBasedWidth = maxHeight * aspectRatio;
              
              if (widthBasedHeight <= maxHeight) {
                finalHeight = widthBasedHeight;
              } else {
                finalWidth = heightBasedWidth;
                finalHeight = maxHeight;
              }
            } else {
              // Only maxWidth specified, calculate height
              finalHeight = maxWidth / aspectRatio;
            }
          }
          
          // Create high-res canvas
          const scale = 4;
          const canvas = document.createElement('canvas');
          canvas.width = finalWidth * scale;
          canvas.height = finalHeight * scale;
          
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          }
          
          const dataUrl = canvas.toDataURL('image/png', 1.0);
          this.pdf.addImage(dataUrl, 'PNG', x, y, finalWidth, finalHeight);
          
          console.log(`Added ${url} at ${x},${y} with size ${finalWidth}x${finalHeight}`);
          resolve();
        };
        
        img.onerror = () => {
          console.error('Error loading image:', url);
          resolve();
        };
        
        img.src = window.location.origin + url;
      });
    } catch (error) {
      console.error('Error adding SVG image:', error);
    }
  }

  private async drawPageFooter(t?: (key: string, options?: any) => string, clientName?: string): Promise<void> {
    const footerY = this.pageHeight - 18;
    
    // Línea divisora superior del footer - más visible
    this.pdf.setDrawColor(180, 180, 180);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.margin, footerY - 8, this.pageWidth - this.margin, footerY - 8);
    
    // Espacio adicional después de la línea
    const footerContentY = footerY;
    
    // Powered by text (izquierda)
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(120, 120, 120);
    const poweredByX = this.margin;
    const poweredByY = footerContentY + 3;
    this.pdf.text(t ? t('statistics.powered_by') : 'Powered by', poweredByX, poweredByY);
    
    // Logo de Pulpou horizontal
    const pulpouLogoX = poweredByX + 20;
    const pulpouLogoY = footerContentY - 2;
    await this.addSvgImage(
      pulpouBrand.logoHorizontal.url, 
      pulpouLogoX, 
      pulpouLogoY, 
      32, 
      10,
      true
    );
    
    // Nombre del cliente (derecha)
    if (clientName) {
      this.pdf.setFontSize(11);
      this.pdf.setTextColor(60, 60, 60);
      this.pdf.setFont('helvetica', 'bold');
      const textWidth = this.pdf.getTextWidth(clientName);
      this.pdf.text(clientName, this.pageWidth - this.margin - textWidth, footerContentY + 3);
    }
    
    // Reset font
    this.pdf.setFont('helvetica', 'normal');
  }

  private formatCurrency(amount: number | null | undefined): string {
    const validAmount = amount || 0;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(validAmount);
  }

  private formatNumber(num: number | null | undefined): string {
    const validNum = num || 0;
    return new Intl.NumberFormat('es-ES').format(validNum);
  }

  private async drawDonutChart(
    centerX: number,
    centerY: number,
    radius: number,
    data: Array<{ cantidad: number; porcentaje: number }>,
    colors: string[]
  ): Promise<void> {
    const innerRadius = radius * 0.6; // Inner radius for donut hole
    
    // Calculate total percentage to check if we need to add "Others"
    const totalPercentage = data.reduce((sum, item) => sum + parseFloat(String(item.porcentaje || 0)), 0);
    
    // Create working data - add "Others" if percentages don't add up to ~100
    let workingData = [...data];
    if (totalPercentage < 99) {
      // Add remaining percentage as "Others"
      const remainingPercentage = 100 - totalPercentage;
      workingData.push({
        cantidad: 0,
        porcentaje: remainingPercentage
      });
    }
    
    // Draw each segment
    let currentAngle = -Math.PI / 2; // Start at top (in radians)
    
    workingData.forEach((item, index) => {
      const percentage = parseFloat(String(item.porcentaje || 0));
      if (percentage < 0.1) return; // Skip tiny segments
      
      const angleSize = (percentage / 100) * 2 * Math.PI;
      const endAngle = currentAngle + angleSize;
      
      // Get color for this segment
      const color = index < data.length ? colors[index % colors.length] : '#CCCCCC'; // Gray for "Others"
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      this.pdf.setFillColor(r, g, b);
      this.pdf.setDrawColor(r, g, b);
      this.pdf.setLineWidth(2); // Thicker line to cover gaps
      
      // Draw the donut segment with many points for smooth curves
      const steps = Math.max(30, Math.ceil(angleSize * 50)); // More steps for larger angles
      
      // Start from inner radius
      const startInnerX = centerX + Math.cos(currentAngle) * innerRadius;
      const startInnerY = centerY + Math.sin(currentAngle) * innerRadius;
      this.pdf.moveTo(startInnerX, startInnerY);
      
      // Draw outer arc
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const angle = currentAngle + angleSize * t;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        this.pdf.lineTo(x, y);
      }
      
      // Draw line to inner radius at end angle
      const endInnerX = centerX + Math.cos(endAngle) * innerRadius;
      const endInnerY = centerY + Math.sin(endAngle) * innerRadius;
      this.pdf.lineTo(endInnerX, endInnerY);
      
      // Draw inner arc (backwards)
      for (let i = steps; i >= 0; i--) {
        const t = i / steps;
        const angle = currentAngle + angleSize * t;
        const x = centerX + Math.cos(angle) * innerRadius;
        const y = centerY + Math.sin(angle) * innerRadius;
        this.pdf.lineTo(x, y);
      }
      
      // Close the path
      this.pdf.lineTo(startInnerX, startInnerY);
      
      // Fill and stroke with same color
      this.pdf.fill();
      this.pdf.stroke();
      
      currentAngle = endAngle;
    });
    
    // Draw white center circle to clean up the donut hole
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.setDrawColor(255, 255, 255);
    this.pdf.setLineWidth(1);
    this.pdf.circle(centerX, centerY, innerRadius + 1, 'FD');
    
    // Draw percentage labels for original data only
    currentAngle = -Math.PI / 2;
    data.forEach((item) => {
      const percentage = parseFloat(String(item.porcentaje || 0));
      const angleSize = (percentage / 100) * 2 * Math.PI;
      
      if (percentage >= 3) { // Only show label if percentage is significant
        const labelAngle = currentAngle + angleSize / 2;
        const labelRadius = radius + 8;
        const labelX = centerX + Math.cos(labelAngle) * labelRadius;
        const labelY = centerY + Math.sin(labelAngle) * labelRadius;
        
        this.pdf.setFontSize(9);
        this.pdf.setTextColor(0, 0, 0); // Black text
        this.pdf.text(`${percentage.toFixed(1)}%`, labelX, labelY + 2, { align: 'center' });
      }
      
      currentAngle += angleSize;
    });
  }

  private getDisplayDate(dateString: string): Date {
    let date: Date;
    if (dateString) {
      if (!dateString.includes('T')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts.map(Number);
          date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        date = new Date();
      }
    } else {
      date = new Date();
    }

    return date;
  }

  private formatDate(dateString: string, t: (key: string, options?: any) => string): string {
    const date = this.getDisplayDate(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const monthKey = `statistics.months.${month - 1}`;
    const monthName = t(monthKey);

    // Format: "23 de Junio 2024" or "June 23, 2024" depending on language
    const lang = t('statistics.language_code');
    if (lang === 'es' || lang === 'pt') {
      return `${day} ${t('statistics.date_of')} ${monthName} ${year}`;
    } else {
      return `${monthName} ${day}, ${year}`;
    }
  }

  private drawHeader(title: string, subtitle?: string): void {
    this.currentY = 25; // Comenzar más abajo
    
    // Title
    this.pdf.setFontSize(20);
    this.pdf.setTextColor(33, 33, 33);
    this.pdf.text(title, this.margin, this.currentY);
    
    if (subtitle) {
      this.currentY += 8;
      this.pdf.setFontSize(12);
      this.pdf.setTextColor(100, 100, 100);
      this.pdf.text(subtitle, this.margin, this.currentY);
    }
    
    this.currentY += 15;
  }

  private drawMetricCard(x: number, y: number, width: number, height: number, 
    title: string, value: string, color: string, icon?: string): void {
    
    // Convert hex color to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 245, g: 247, b: 250 };
    };
    
    const rgb = hexToRgb(color);
    
    // Card background with subtle color tint
    // Mix the color with white for a light background
    const bgR = Math.floor(rgb.r * 0.12 + 255 * 0.88);
    const bgG = Math.floor(rgb.g * 0.12 + 255 * 0.88);
    const bgB = Math.floor(rgb.b * 0.12 + 255 * 0.88);
    
    // Draw rounded rectangle with colored border
    this.pdf.setFillColor(bgR, bgG, bgB);
    this.pdf.setDrawColor(rgb.r, rgb.g, rgb.b);
    this.pdf.setLineWidth(1);
    this.pdf.roundedRect(x, y, width, height, 5, 5, 'FD');
    
    // Center X position for text
    const centerX = x + width / 2;
    
    // Title - centered
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(100, 100, 100);
    this.pdf.text(title, centerX, y + 14, { align: 'center' });
    
    // Value - centered and larger with color
    this.pdf.setFontSize(24);
    this.pdf.setTextColor(rgb.r * 0.85, rgb.g * 0.85, rgb.b * 0.85); // Slightly darker version of the color
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(value, centerX, y + 32, { align: 'center' });
    this.pdf.setFont('helvetica', 'normal');
    
    // Reset line width
    this.pdf.setLineWidth(0.5);
  }

  async generateCoverPage(options: ExportOptions): Promise<void> {
    const { tableDate, filters, activeFilterName, clientName, t } = options;
    
    // Background
    this.pdf.setFillColor(250, 250, 250);
    this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, 'F');
    
    // Nombre del cliente en lugar del logo
    const clientNameY = 45;
    if (clientName) {
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(28);
      this.pdf.setTextColor(33, 33, 33);
      this.pdf.text(clientName, this.pageWidth / 2, clientNameY, { align: 'center' });
    }
    
    // Title - más espacio después del nombre del cliente
    const titleY = clientNameY + 35;
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(32);
    this.pdf.setTextColor(33, 33, 33);
    const pageTitle = t 
      ? t('statistics.product_statistics')
      : 'Estadísticas de Productos';
    this.pdf.text(pageTitle, this.pageWidth / 2, titleY, { align: 'center' });
    
    // Table info - solo fecha sin número de toma
    this.pdf.setFontSize(20);
    this.pdf.setTextColor(66, 66, 66);
    this.pdf.text(`${options.t('statistics.capture_of')} ${this.formatDate(tableDate, options.t)}`, this.pageWidth / 2, titleY + 15, { align: 'center' });
    
    // Active filter name if present
    if (activeFilterName) {
      this.pdf.setFontSize(16);
      this.pdf.setTextColor(59, 130, 246); // Blue color
      const filterLabel = options.t ? options.t('statistics.filter_label') : 'Filtro';
      this.pdf.text(`${filterLabel}: ${activeFilterName}`, this.pageWidth / 2, titleY + 30, { align: 'center' });
    }
    
    // Filters section - solo si hay filtros aplicados
    const filterLines: string[] = [];
    
    // Búsqueda global
    if (filters.global_search) {
      filterLines.push(`Búsqueda: "${filters.global_search}"`);
    }
    
    // Operadores de búsqueda
    if (filters.search_operators && Array.isArray(filters.search_operators) && filters.search_operators.length > 0) {
      filters.search_operators.forEach((op: any) => {
        const operatorSymbol = op.operator === 'OR' ? 'OR' : op.operator === 'AND' ? 'AND' : 'NOT';
        filterLines.push(`${operatorSymbol}: ${op.term}`);
      });
    }
    
    // Filtros de marca, categoría y sitio
    // Buscar las claves dinámicas para marca, sitio y categoría
    const marcaKey = Object.keys(filters).find(key => key === 'Marca' || key.toLowerCase().includes('marca')) || 'Marca';
    const sitioKey = Object.keys(filters).find(key => key === 'Sitio' || key === 'sitio') || 'Sitio';
    const categoriaKey = Object.keys(filters).find(key => key === 'Cat' || key === 'categoria' || key.toLowerCase() === 'cat') || 'Cat';
    
    if (filters[marcaKey]) {
      const marcas = filters[marcaKey].split('|');
      marcas.forEach((marca: string) => {
        if (marca.trim()) {
          filterLines.push(`Marca: ${marca.trim()}`);
        }
      });
    }
    if (filters[categoriaKey]) {
      const categorias = filters[categoriaKey].split('|');
      categorias.forEach((categoria: string) => {
        if (categoria.trim()) {
          filterLines.push(`Categoría: ${categoria.trim()}`);
        }
      });
    }
    if (filters[sitioKey]) {
      const sitios = filters[sitioKey].split('|');
      sitios.forEach((sitio: string) => {
        if (sitio.trim()) {
          filterLines.push(`Sitio: ${sitio.trim()}`);
        }
      });
    }
    
    // Rango de precio
    if (filters.price_from || filters.price_to) {
      const priceFrom = filters.price_from ? `$${parseInt(filters.price_from).toLocaleString('es-AR')}` : 'Sin mínimo';
      const priceTo = filters.price_to ? `$${parseInt(filters.price_to).toLocaleString('es-AR')}` : 'Sin máximo';
      filterLines.push(`Rango de precio: ${priceFrom} - ${priceTo}`);
    }
    
    // Posición máxima (página y orden)
    if (filters.max_page) {
      filterLines.push(`Página máxima: ${filters.max_page}`);
    }
    if (filters.max_order) {
      filterLines.push(`Orden máximo: ${filters.max_order}`);
    }
    
    // Filtro de descuentos
    if (filters.descuento_filter && filters.descuento_filter !== 'all') {
      const descuentoText = filters.descuento_filter === 'with_discount' ? 'Con descuentos' : 'Sin descuentos';
      filterLines.push(`Descuentos: ${descuentoText}`);
    }
    
    // Solo mostrar la sección de filtros si hay alguno aplicado
    if (filterLines.length > 0) {
      const filtersY = titleY + (activeFilterName ? 50 : 35);
      
      // Título de filtros
      this.pdf.setFontSize(14);
      this.pdf.setTextColor(100, 100, 100);
      this.pdf.text('Criterios de filtrado:', this.pageWidth / 2, filtersY, { align: 'center' });
      
      // Configuración de pills
      const pillHeight = 6;
      const pillPadding = 8;
      const pillGap = 4;
      const maxWidth = this.pageWidth - 60;
      let currentY = filtersY + 12;
      this.pdf.setFontSize(9);
      
      // Calcular el ancho de cada pill y organizarlas en filas
      const pills: { text: string; width: number; type: string }[] = [];
      filterLines.forEach(line => {
        // Limitar longitud de texto si es muy largo
        const maxTextLength = 60;
        const displayText = line.length > maxTextLength ? line.substring(0, maxTextLength) + '...' : line;
        const textWidth = this.pdf.getTextWidth(displayText);
        const pillWidth = textWidth + pillPadding;
        
        // Determinar el tipo de filtro para asignar color
        let type = 'default';
        if (line.startsWith('Búsqueda:')) type = 'search';
        else if (line.startsWith('OR:') || line.startsWith('AND:') || line.startsWith('NOT:')) {
          type = line.startsWith('OR:') ? 'or' : line.startsWith('AND:') ? 'and' : 'not';
        }
        else if (line.startsWith('Marca:')) type = 'brand';
        else if (line.startsWith('Categoría:')) type = 'category';
        else if (line.startsWith('Sitio:')) type = 'site';
        else if (line.startsWith('Rango de precio:')) type = 'price';
        else if (line.startsWith('Página máxima:') || line.startsWith('Orden máximo:')) type = 'position';
        else if (line.startsWith('Descuentos:')) type = 'discount';
        
        pills.push({ text: displayText, width: pillWidth, type });
      });
      
      // Organizar pills en filas para centrarlas
      const rows: { pills: typeof pills; totalWidth: number }[] = [];
      let currentRow: typeof pills = [];
      let currentRowWidth = 0;
      
      pills.forEach(pill => {
        if (currentRowWidth + pill.width + (currentRow.length > 0 ? pillGap : 0) > maxWidth && currentRow.length > 0) {
          // Guardar la fila actual
          rows.push({ pills: [...currentRow], totalWidth: currentRowWidth - pillGap });
          currentRow = [pill];
          currentRowWidth = pill.width;
        } else {
          currentRow.push(pill);
          currentRowWidth += pill.width + (currentRow.length > 1 ? pillGap : 0);
        }
      });
      
      // Agregar la última fila
      if (currentRow.length > 0) {
        rows.push({ pills: currentRow, totalWidth: currentRowWidth - pillGap });
      }
      
      // Dibujar las pills centradas por fila
      rows.forEach(row => {
        // Calcular el offset para centrar la fila
        const rowStartX = (this.pageWidth - row.totalWidth) / 2;
        let pillX = rowStartX;
        
        row.pills.forEach(pill => {
          // Dibujar el fondo de la pill con colores según el tipo
        const colors: Record<string, { bg: [number, number, number]; border: [number, number, number]; text: [number, number, number] }> = {
          search: { bg: [236, 253, 245], border: [167, 243, 208], text: [6, 78, 59] }, // Verde claro
          or: { bg: [219, 234, 254], border: [147, 197, 253], text: [30, 64, 175] }, // Azul
          and: { bg: [209, 250, 229], border: [110, 231, 183], text: [6, 95, 70] }, // Verde
          not: { bg: [254, 226, 226], border: [252, 165, 165], text: [153, 27, 27] }, // Rojo
          brand: { bg: [243, 232, 255], border: [196, 181, 253], text: [88, 28, 135] }, // Morado
          category: { bg: [254, 240, 138], border: [253, 224, 71], text: [113, 63, 18] }, // Amarillo
          site: { bg: [226, 232, 240], border: [148, 163, 184], text: [51, 65, 85] }, // Gris azulado
          price: { bg: [254, 235, 200], border: [253, 186, 116], text: [124, 45, 18] }, // Naranja claro
          position: { bg: [232, 229, 255], border: [167, 139, 250], text: [67, 56, 202] }, // Indigo
          discount: { bg: [252, 231, 243], border: [251, 182, 206], text: [157, 23, 77] }, // Rosa
          default: { bg: [240, 243, 246], border: [209, 213, 219], text: [55, 65, 81] } // Gris
        };
        
        const color = colors[pill.type] || colors.default;
        
          this.pdf.setFillColor(color.bg[0], color.bg[1], color.bg[2]);
          this.pdf.setDrawColor(color.border[0], color.border[1], color.border[2]);
          this.pdf.setLineWidth(0.3);
          this.pdf.roundedRect(pillX, currentY - pillHeight + 2, pill.width, pillHeight, 3, 3, 'FD');
          
          // Dibujar el texto
          this.pdf.setTextColor(color.text[0], color.text[1], color.text[2]);
          this.pdf.text(pill.text, pillX + pillPadding / 2, currentY);
          
          pillX += pill.width + pillGap;
        });
        
        currentY += pillHeight + pillGap;
      });
      
    }
    
    // Footer con logo de Pulpou - más espacio desde el bottom
    const footerY = this.pageHeight - 65; // Más espacio desde abajo
    
    // Powered by text
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(120, 120, 120);
    this.pdf.text('Powered by', this.pageWidth / 2, footerY + 5, { align: 'center' });
    
    // PULPOU logo vertical - más pequeño pero bien centrado
    const pulpouVerticalMaxSize = 28; // Más pequeño
    const pulpouLogoX = this.pageWidth / 2 - pulpouVerticalMaxSize / 2;
    const pulpouLogoY = footerY + 10;
    
    await this.addSvgImage(
      pulpouBrand.logoVertical.url, 
      pulpouLogoX, 
      pulpouLogoY, 
      pulpouVerticalMaxSize, 
      pulpouVerticalMaxSize,
      true
    );
    
    // Reset font
    this.pdf.setFont('helvetica', 'normal');
  }

  async generateOverviewPage(statistics: any, t: (key: string, options?: any) => string, clientName?: string): Promise<void> {
    this.pdf.addPage();
    
    // Header con mejor espaciado
    this.currentY = 25;
    this.pdf.setFontSize(24);
    this.pdf.setTextColor(33, 33, 33);
    this.pdf.text(t('statistics.overview_title'), this.margin, this.currentY);
    
    this.currentY += 8;
    this.pdf.setFontSize(14);
    this.pdf.setTextColor(100, 100, 100);
    this.pdf.text(t('statistics.overview_subtitle'), this.margin, this.currentY);
    
    this.currentY += 30; // Más espacio antes de las tarjetas
    
    // Metric cards grid - 2 filas de 3 tarjetas
    // Calcular el ancho disponible dentro de los márgenes (mismo ancho que la línea del footer)
    const availableWidth = this.pageWidth - (2 * this.margin);
    const cardsPerRow = 3;
    const gap = 10; // Espacio entre tarjetas
    const totalGapWidth = gap * (cardsPerRow - 1);
    const cardWidth = (availableWidth - totalGapWidth) / cardsPerRow;
    const cardHeight = 40; // Altura de las tarjetas
    const startX = this.margin;
    
    // General Statistics cards
    // Row 1 - 3 tarjetas
    this.drawMetricCard(
      startX, this.currentY, cardWidth, cardHeight,
      t('statistics.total_publications'),
      this.formatNumber(statistics.totalPublicaciones),
      '#DBEAFE'
    );
    
    this.drawMetricCard(
      startX + cardWidth + gap, this.currentY, cardWidth, cardHeight,
      t('statistics.publications_with_discount'),
      this.formatNumber(statistics.publicacionesConDescuento),
      '#D1FAE5'
    );
    
    this.drawMetricCard(
      startX + 2 * (cardWidth + gap), this.currentY, cardWidth, cardHeight,
      t('statistics.percentage_with_discount'),
      `${parseFloat(String(statistics.porcentajeDescuento || 0)).toFixed(1)}%`,
      '#FED7AA'
    );
    
    // Row 2 - 3 tarjetas
    this.currentY += cardHeight + gap + 5;
    
    this.drawMetricCard(
      startX, this.currentY, cardWidth, cardHeight,
      t('statistics.average_discount'),
      `${parseFloat(String(statistics.descuentoPromedio || 0)).toFixed(1)}%`,
      '#E9D5FF'
    );
    
    this.drawMetricCard(
      startX + cardWidth + gap, this.currentY, cardWidth, cardHeight,
      t('statistics.median_price'),
      this.formatCurrency(statistics.precioMediano),
      '#FEF3C7'
    );
    
    this.drawMetricCard(
      startX + 2 * (cardWidth + gap), this.currentY, cardWidth, cardHeight,
      t('statistics.total_brands') + ' / ' + t('statistics.total_categories'),
      `${this.formatNumber(statistics.cantidadMarcas)} / ${this.formatNumber(statistics.cantidadCategorias)}`,
      '#E9D5FF'
    );
    
    // Agregar footer
    await this.drawPageFooter(t, clientName);
  }

  async generateShareTable(
    data: Array<{ marca?: string; categoria?: string; sitio?: string; cantidad: number; porcentaje: number }>,
    title: string,
    subtitle: string,
    columnName: string,
    colors: string[],
    t: (key: string, options?: any) => string,
    clientName?: string
  ): Promise<void> {
    this.pdf.addPage();
    this.drawHeader(title, subtitle);
    
    // Layout: Donut chart on left, table on right
    const chartSize = 80; // Tamaño del gráfico de dona
    const chartX = this.margin + 20;
    const chartY = this.currentY + 20;
    const tableX = chartX + chartSize + 30; // Espacio entre gráfico y tabla
    const tableWidth = this.pageWidth - tableX - this.margin;
    
    // Draw donut chart
    await this.drawDonutChart(chartX + chartSize/2, chartY + chartSize/2, chartSize/2 - 5, data, colors);
    
    // Check if autoTable is available
    if (this.pdf.autoTable) {
      // Prepare table data
      const tableData = data.map((item, index) => {
        const name = item.marca || item.categoria || item.sitio || 'Sin datos';
        return [
          name,
          this.formatNumber(item.cantidad),
          `${typeof item.porcentaje === 'number' ? item.porcentaje.toFixed(1) : parseFloat(item.porcentaje).toFixed(1)}%`
        ];
      });
      
      // Draw table with autoTable - positioned to the right of the chart
      this.pdf.autoTable({
        head: [[columnName, t('statistics.quantity'), t('statistics.percentage')]],
        body: tableData,
        startY: chartY,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontSize: 11,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 10,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: tableWidth * 0.5, halign: 'left', cellPadding: { left: 15 } },
          1: { cellWidth: tableWidth * 0.25, halign: 'right' },
          2: { cellWidth: tableWidth * 0.25, halign: 'right' }
        },
        margin: { left: tableX, right: this.margin },
        tableWidth: tableWidth,
        didDrawCell: (data: any) => {
          // Add color indicator inside the cell before the text
          if (data.section === 'body' && data.column.index === 0) {
            const color = colors[data.row.index % colors.length];
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            this.pdf.setFillColor(r, g, b);
            this.pdf.circle(data.cell.x + 8, data.cell.y + data.cell.height / 2, 2, 'F');
          }
        }
      });
      
      // Summary - positioned below the table on the right side
      const finalY = this.pdf.lastAutoTable.finalY + 10;
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(75, 85, 99);
      this.pdf.text(`${t('statistics.total_elements')}: ${data.length}`, tableX, finalY);
      this.pdf.text(
        `${t('statistics.total_publications')}: ${this.formatNumber(data.reduce((sum, item) => sum + item.cantidad, 0))}`,
        tableX,
        finalY + 6
      );
    } else {
      // Fallback: Draw donut chart and table manually without autoTable
      // Draw donut chart (same as above)
      await this.drawDonutChart(chartX + chartSize/2, chartY + chartSize/2, chartSize/2 - 5, data, colors);
      
      // Draw table manually with adjusted position
      this.currentY = chartY;
      await this.drawManualTable(data, columnName, colors, t, tableX, tableWidth, clientName);
    }
    
    // Add footer to the page
    await this.drawPageFooter(t, clientName);
  }

  private async drawManualTable(
    data: Array<{ marca?: string; categoria?: string; sitio?: string; cantidad: number; porcentaje: number }>,
    columnName: string,
    colors: string[],
    t: (key: string, options?: any) => string,
    startX?: number,
    tableWidth?: number,
    clientName?: string
  ): Promise<void> {
    const xPos = startX || this.margin;
    const totalWidth = tableWidth || (this.pageWidth - 2 * this.margin);
    const colWidths = [totalWidth * 0.5, totalWidth * 0.25, totalWidth * 0.25];
    const rowHeight = 8;
    let currentY = this.currentY;
    
    // Header
    this.pdf.setFillColor(59, 130, 246);
    this.pdf.rect(xPos, currentY, colWidths[0] + colWidths[1] + colWidths[2], rowHeight, 'F');
    
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(11);
    this.pdf.text(columnName, xPos + 5, currentY + 5);
    // Right align quantity and percentage headers
    this.pdf.text(t('statistics.quantity'), xPos + colWidths[0] + colWidths[1] - 5, currentY + 5, { align: 'right' });
    this.pdf.text(t('statistics.percentage'), xPos + colWidths[0] + colWidths[1] + colWidths[2] - 5, currentY + 5, { align: 'right' });
    
    currentY += rowHeight;
    
    // Body
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(10);
    
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      // Check if we need a new page
      if (currentY + rowHeight > this.pageHeight - 35) { // Leave more space for footer
        this.pdf.addPage();
        await this.drawPageFooter(t, clientName);
        currentY = this.margin + 20; // Start a bit lower on new pages
      }
      
      // Alternate row colors
      if (index % 2 === 1) {
        this.pdf.setFillColor(245, 245, 245);
        this.pdf.rect(xPos, currentY, colWidths[0] + colWidths[1] + colWidths[2], rowHeight, 'F');
      }
      
      // Color indicator inside the cell
      const color = colors[index % colors.length];
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      this.pdf.setFillColor(r, g, b);
      this.pdf.circle(xPos + 8, currentY + rowHeight / 2, 2, 'F');
      
      // Text with padding for color indicator
      const name = item.marca || item.categoria || item.sitio || 'Sin datos';
      this.pdf.setTextColor(0, 0, 0);
      this.pdf.text(name, xPos + 15, currentY + 5, { maxWidth: colWidths[0] - 20 });
      this.pdf.text(this.formatNumber(item.cantidad), xPos + colWidths[0] + colWidths[1] - 5, currentY + 5, { align: 'right' });
      const formattedPercentage = typeof item.porcentaje === 'number' ? item.porcentaje.toFixed(1) : parseFloat(item.porcentaje).toFixed(1);
      this.pdf.text(`${formattedPercentage}%`, xPos + colWidths[0] + colWidths[1] + colWidths[2] - 5, currentY + 5, { align: 'right' });
      
      currentY += rowHeight;
    }
    
    // Summary
    currentY += 10;
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(75, 85, 99);
    this.pdf.text(`${t('statistics.total_elements')}: ${data.length}`, xPos, currentY);
    this.pdf.text(
      `${t('statistics.total_publications')}: ${this.formatNumber(data.reduce((sum, item) => sum + item.cantidad, 0))}`,
      xPos,
      currentY + 6
    );
  }

  private async drawPriceRangeBarChart(
    data: Array<{ rango: string; cantidad: number; porcentaje: number; desde: number; hasta: number }>,
    startY: number,
    chartHeight: number
  ): Promise<void> {
    const maxBarWidth = 100;
    const priceRangeWidth = 80;
    const totalWidth = maxBarWidth + priceRangeWidth + 20;
    const chartX = (this.pageWidth - totalWidth) / 2;
    const barHeight = Math.min(chartHeight / Math.max(data.length, 1) - 3, 10);
    const maxValue = Math.max(...data.map(item => item.cantidad), 1);

    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
      '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
    ];

    let currentY = startY;

    data.forEach((item, index) => {
      const barWidth = (item.cantidad / maxValue) * maxBarWidth;
      const color = colors[index % colors.length];
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      this.pdf.setFillColor(r, g, b);
      this.pdf.roundedRect(chartX, currentY, barWidth, barHeight, 2, 2, 'F');

      this.pdf.setFontSize(9);
      const formattedPercentage = typeof item.porcentaje === 'number'
        ? item.porcentaje.toFixed(1)
        : parseFloat(String(item.porcentaje)).toFixed(1);

      if (barWidth > 40) {
        this.pdf.setTextColor(255, 255, 255);
        this.pdf.text(
          `${item.cantidad} (${formattedPercentage}%)`,
          chartX + barWidth - 5,
          currentY + barHeight / 2 + 1,
          { align: 'right' }
        );
      } else {
        this.pdf.setTextColor(0, 0, 0);
        this.pdf.text(
          `${item.cantidad} (${formattedPercentage}%)`,
          chartX + barWidth + 5,
          currentY + barHeight / 2 + 1
        );
      }

      this.pdf.setFontSize(8);
      this.pdf.setTextColor(100, 100, 100);
      const priceRange = `${this.formatCurrency(item.desde)} - ${this.formatCurrency(item.hasta)}`;
      this.pdf.text(
        priceRange,
        chartX + maxBarWidth + 15,
        currentY + barHeight / 2 + 1
      );

      currentY += barHeight + 3;
    });
  }

  async generatePriceRangePage(data: any[], t: (key: string, options?: any) => string, clientName?: string): Promise<void> {
    if (!data || data.length === 0) {
      return;
    }

    this.pdf.addPage();
    this.drawHeader(t('statistics.price_range_analysis'), t('statistics.price_range_subtitle'));

    const footerY = this.pageHeight - 30;
    const availableHeight = footerY - this.currentY - 20;
    const chartHeight = Math.min(data.length * 12, 120);
    const chartY = this.currentY + (availableHeight - chartHeight) / 2;

    await this.drawPriceRangeBarChart(data, chartY, chartHeight);
    await this.drawPageFooter(t, clientName);
  }

  private async drawInstallmentsBarChart(
    data: Array<{ label: string; cantidad: number; porcentaje: number }>,
    startY: number,
    chartHeight: number
  ): Promise<void> {
    const maxBarWidth = 110;
    const labelWidth = 60;
    const totalWidth = maxBarWidth + labelWidth + 20;
    const centeredX = (this.pageWidth - totalWidth) / 2;
    const finalChartX = centeredX + labelWidth;
    const barHeight = Math.min(chartHeight / Math.max(data.length, 1) - 3, 10);

    const maxValue = Math.max(...data.map(item => item.cantidad), 1);

    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
      '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
    ];

    let currentY = startY;

    data.forEach((item, index) => {
      const barWidth = (item.cantidad / maxValue) * maxBarWidth;
      const color = colors[index % colors.length];
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      // Label to the left
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(80, 80, 80);
      this.pdf.text(
        item.label,
        finalChartX - 5,
        currentY + barHeight / 2 + 1,
        { align: 'right' }
      );

      // Bar
      this.pdf.setFillColor(r, g, b);
      this.pdf.roundedRect(finalChartX, currentY, barWidth, barHeight, 2, 2, 'F');

      const formattedPercentage = item.porcentaje.toFixed(1);
      if (barWidth > 40) {
        this.pdf.setTextColor(255, 255, 255);
        this.pdf.text(
          `${item.cantidad} (${formattedPercentage}%)`,
          finalChartX + barWidth - 5,
          currentY + barHeight / 2 + 1,
          { align: 'right' }
        );
      } else {
        this.pdf.setTextColor(0, 0, 0);
        this.pdf.text(
          `${item.cantidad} (${formattedPercentage}%)`,
          finalChartX + barWidth + 5,
          currentY + barHeight / 2 + 1
        );
      }

      currentY += barHeight + 3;
    });
  }

  async generateInstallmentsPage(data: any[], t: (key: string, options?: any) => string, clientName?: string): Promise<void> {
    if (!data || data.length === 0) {
      return;
    }

    const chartData = data.map(item => {
      const label = item.installments === null
        ? t('statistics.installments.no_installments')
        : t(
            item.installments === 1
              ? 'statistics.installments.installment_label'
              : 'statistics.installments.installment_label_plural',
            { count: item.installments }
          );

      return {
        label,
        cantidad: item.cantidad,
        porcentaje: typeof item.porcentaje === 'number' ? item.porcentaje : parseFloat(item.porcentaje)
      };
    });

    this.pdf.addPage();
    this.drawHeader(t('statistics.installments.distribution_title'), t('statistics.installments.distribution_subtitle'));

    const footerY = this.pageHeight - 30;
    const availableHeight = footerY - this.currentY - 20;
    const chartHeight = Math.min(chartData.length * 12, 120);
    const chartY = this.currentY + (availableHeight - chartHeight) / 2;

    await this.drawInstallmentsBarChart(chartData, chartY, chartHeight);
    await this.drawPageFooter(t, clientName);
  }

  private async drawManualPriceRangeTable(data: any[], t: (key: string, options?: any) => string, clientName?: string): Promise<void> {
    const colWidths = [40, 100, 50, 50];
    const rowHeight = 8;
    const startX = this.margin;
    let currentY = this.currentY;
    
    // Header
    this.pdf.setFillColor(59, 130, 246);
    this.pdf.rect(startX, currentY, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], rowHeight, 'F');
    
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(11);
    this.pdf.text(t('statistics.range'), startX + 5, currentY + 5);
    this.pdf.text(t('statistics.prices'), startX + colWidths[0] + 5, currentY + 5);
    // Right align quantity and percentage headers
    this.pdf.text(t('statistics.quantity'), startX + colWidths[0] + colWidths[1] + colWidths[2] - 5, currentY + 5, { align: 'right' });
    this.pdf.text(t('statistics.percentage'), startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] - 5, currentY + 5, { align: 'right' });
    
    currentY += rowHeight;
    
    // Body
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(10);
    
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      // Check if we need a new page
      if (currentY + rowHeight > this.pageHeight - 35) { // Leave more space for footer
        this.pdf.addPage();
        await this.drawPageFooter(t, clientName);
        currentY = this.margin + 20; // Start a bit lower on new pages
      }
      
      // Alternate row colors
      if (index % 2 === 1) {
        this.pdf.setFillColor(245, 245, 245);
        this.pdf.rect(startX, currentY, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], rowHeight, 'F');
      }
      
      // Text
      this.pdf.text(item.rango, startX + 5, currentY + 5, { maxWidth: colWidths[0] - 10 });
      this.pdf.text(
        `${this.formatCurrency(item.desde)} - ${this.formatCurrency(item.hasta)}`,
        startX + colWidths[0] + 5,
        currentY + 5,
        { maxWidth: colWidths[1] - 10 }
      );
      this.pdf.text(
        this.formatNumber(item.cantidad),
        startX + colWidths[0] + colWidths[1] + colWidths[2] - 5,
        currentY + 5,
        { align: 'right' }
      );
      this.pdf.text(
        `${typeof item.porcentaje === 'number' ? item.porcentaje.toFixed(1) : parseFloat(item.porcentaje).toFixed(1)}%`,
        startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] - 5,
        currentY + 5,
        { align: 'right' }
      );
      
      currentY += rowHeight;
    }
  }

  save(filename: string): void {
    this.pdf.save(filename);
  }
}

export const generateStatisticsPDF = async (options: ExportOptions): Promise<void> => {
  console.log('generateStatisticsPDF called with options:', options);
  
  try {
    console.log('Creating PDFGenerator instance...');
    const generator = new PDFGenerator();
    const { statistics, t, tableDate } = options;
    
    if (!statistics) {
      throw new Error('No hay datos de estadísticas disponibles');
    }
    
    if (!t) {
      throw new Error('Función de traducción no disponible');
    }
    
    console.log('Generating cover page...');
    // Cover page
    await generator.generateCoverPage(options);
    
    console.log('Generating overview page...');
    // Overview page
    await generator.generateOverviewPage(statistics, t, options.clientName);
    
    // Generate general statistics pages
    
    // Brand share
    if (statistics.shareMarcias?.data && statistics.shareMarcias.data.length > 0) {
      await generator.generateShareTable(
        statistics.shareMarcias.data,
        t('statistics.brand_share'),
        t('statistics.brand_share_subtitle'),
        t('statistics.brand'),
        ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'],
        t,
        options.clientName
      );
    }
    
    // Category share
    if (statistics.shareCategorias?.data && statistics.shareCategorias.data.length > 0) {
      await generator.generateShareTable(
        statistics.shareCategorias.data,
        t('statistics.category_share'),
        t('statistics.category_share_subtitle'),
        t('statistics.category'),
        ['#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#84CC16'],
        t,
        options.clientName
      );
    }
    
    // Site listings
    if (statistics.publicacionesPorSitio?.data && statistics.publicacionesPorSitio.data.length > 0) {
      await generator.generateShareTable(
        statistics.publicacionesPorSitio.data,
        t('statistics.publications_by_site'),
        t('statistics.publications_by_site_subtitle'),
        t('statistics.site'),
        ['#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'],
        t,
        options.clientName
      );
    }
    
    // Price ranges
    if (statistics.rangosPreciosData?.data && statistics.rangosPreciosData.data.length > 0) {
      await generator.generatePriceRangePage(statistics.rangosPreciosData.data, t, options.clientName);
    }
    if (statistics.installmentsDistribution?.data && statistics.installmentsDistribution.data.length > 0) {
      await generator.generateInstallmentsPage(statistics.installmentsDistribution.data, t, options.clientName);
    }
    
    // Generate filename - sin número de toma
    // Parse date string to avoid timezone issues
    const dateParts = tableDate.split('T')[0].split('-');
    const year = parseInt(dateParts[0]);
    const monthNum = parseInt(dateParts[1]); // Keep 1-indexed
    const day = parseInt(dateParts[2]);
    const monthKey = `statistics.months.${monthNum - 1}`; // Convert to 0-indexed for translation key
    const month = t(monthKey);
    
    const filename = `${t('statistics.product_statistics')} - ${t('statistics.capture_of')} ${day} ${t('statistics.date_of')} ${month} ${year}.pdf`;
    generator.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
